import { BlockRenderer } from '@/components/page-builder/BlockRenderer';
import PublicLayout from '@/layouts/public-layout';
import { Head } from '@inertiajs/react';

interface PageBlock {
    id: number;
    block_type: string;
    block_component: string;
    position: number;
    settings: Record<string, unknown>;
    content: Record<string, unknown>;
    is_visible: boolean;
}

interface Page {
    id: number;
    title: string;
    slug: string;
    path: string;
    status: string;
    blocks?: PageBlock[];
    collection_type?: string;
    collection_id?: number;
}

interface PageBuilderViewProps {
    page: Page;
    content: Page;
    metadata: Record<string, unknown>;
    blockSourceBindings?: Record<string, Record<string, { alias: string; data: unknown }>>;
    isPreview?: boolean;
}

// Homepage-only: mobile DOM order follows block position. At md+, reshuffle via
// flexbox `order` so tablet/desktop matches the same sequence: the hero (no
// entry → order-0) is followed by journal_preview → how_it_works → value_proposition.
const HOMEPAGE_MD_ORDER: Record<string, string> = {
    home_video_feature: 'md:order-1',
    founder_quote: 'md:order-2',
    journal_preview: 'md:order-3',
    journal_cta: 'md:order-4',
    how_it_works: 'md:order-5',
    value_proposition: 'md:order-6',
    exclusive_access_grid: 'md:order-7',
    fullbleed_interstitial: 'md:order-8',
};

export default function PageBuilderView({ page, content, metadata, isPreview }: PageBuilderViewProps) {
    const blocks = content.blocks || [];
    const Layout = PublicLayout;
    const isHomepage = page.path === '/' || page.slug === '' || page.slug === '/';

    // Extract header configuration from header_config block
    const headerBlock = blocks.find((block) => block.block_type === 'header_config');
    const blockHeaderConfig = (headerBlock?.content as Record<string, unknown> | undefined) || {};

    // Auto-detect agent pages (/the-team/{slug}) and apply agent nav variant:
    // Logo left, tagline + "Book With {name}" CTA, no hamburger
    const isAgentPage = page.path?.startsWith('/the-team/') || false;
    const agentHeaderDefaults = isAgentPage
        ? {
              showMenu: false,
              showCta: true,
              ctaText: 'Book With {{agent.first_name}}',
              ctaAction: 'navigate' as const,
              ctaUrl: '/clients',
              ctaStyle: 'filled' as const,
          }
        : {};

    // Agent defaults apply first, then page-specific block overrides take priority
    const headerConfig = { ...agentHeaderDefaults, ...blockHeaderConfig };

    const metaTitle = typeof metadata.title === 'string' ? metadata.title : page.title;
    const metaDescription = typeof metadata.description === 'string' ? metadata.description : null;
    const metaKeywords = typeof metadata.keywords === 'string' ? metadata.keywords : null;
    const metaRobots = typeof metadata.robots === 'string' ? metadata.robots : null;

    return (
        <Layout headerConfig={headerConfig}>
            <Head title={metaTitle}>
                {metaDescription && <meta name="description" content={metaDescription} />}
                {metaKeywords && <meta name="keywords" content={metaKeywords} />}
                {metaRobots && <meta name="robots" content={metaRobots} />}
            </Head>

            <div className={isHomepage ? 'md:flex md:flex-col' : ''}>
                {isPreview && (
                    <div className="bg-yellow-500 p-2 text-center text-sm font-medium text-yellow-900">Preview Mode - This page is not published</div>
                )}

                {blocks
                    .filter((block) => block.is_visible)
                    .sort((a, b) => a.position - b.position)
                    .map((block) => {
                        if (isHomepage) {
                            // Wrap each block so flexbox `order` can reshuffle at md+.
                            // Blocks without an explicit order fall back to DOM position (hero first).
                            return (
                                <div key={block.id} className={HOMEPAGE_MD_ORDER[block.block_type] ?? ''}>
                                    <BlockRenderer
                                        block={block}
                                        isSelected={false}
                                        selectedElement={null}
                                        onSelectElement={() => {}}
                                        isInPageBuilder={false}
                                    />
                                </div>
                            );
                        }
                        return (
                            <BlockRenderer
                                key={block.id}
                                block={block}
                                isSelected={false}
                                selectedElement={null}
                                onSelectElement={() => {}}
                                isInPageBuilder={false}
                            />
                        );
                    })}
            </div>
        </Layout>
    );
}
